import type { CppPricingModel, CppRateTier } from '~/types/costPerPoint';

/**
 * Validates tiers on the client for inline form feedback. Mirrors the backend rules:
 * first tier starts at 0, bands are contiguous (min = prev.max + 1, inclusive),
 * exactly one open-ended last tier, cpp > 0; FLAT must be a single unbounded tier.
 * @param tiers - The tiers as entered.
 * @param pricingModel - FLAT or TIERED.
 * @returns An error message, or null when valid.
 */
export function validateTiersClient(tiers: Array<CppRateTier>, pricingModel: CppPricingModel): string | null {
  if (tiers.length === 0) {
    return 'A rate card must have at least one tier.';
  }
  for (const tier of tiers) {
    if (!(tier.cpp > 0)) {
      return 'Every tier CPP must be greater than 0.';
    }
  }

  if (pricingModel === 'FLAT') {
    if (tiers.length !== 1) {
      return 'A flat rate card must have exactly one tier.';
    }
    const [only] = tiers;
    if (only.minPoints !== 0 || only.maxPoints !== null) {
      return 'A flat tier must be unbounded (min 0, max unlimited).';
    }
    return null;
  }

  const ordered = [...tiers].sort((a, b) => a.minPoints - b.minPoints);
  if (ordered[0].minPoints !== 0) {
    return 'The first tier must start at 0.';
  }
  for (let i = 0; i < ordered.length; i++) {
    const tier = ordered[i];
    const isLast = i === ordered.length - 1;
    if (isLast) {
      if (tier.maxPoints !== null) {
        return 'The last tier must be open-ended (unlimited).';
      }
    } else {
      if (tier.maxPoints === null) {
        return 'Only the last tier may be unlimited.';
      }
      if (ordered[i + 1].minPoints !== tier.maxPoints + 1) {
        return 'Tiers must be contiguous with no gaps or overlaps.';
      }
    }
  }
  return null;
}
